import React, { useState, useEffect, useCallback, createContext, useContext } from 'react';
import { createPortal } from 'react-dom';
import styled, { css, keyframes } from 'styled-components';
import { X, CheckCircle, AlertCircle, AlertTriangle, Info } from 'lucide-react';
import { theme } from '../../tokens';

const slideIn = keyframes`
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
`;

const slideOut = keyframes`
  from {
    transform: translateX(0);
    opacity: 1;
  }
  to {
    transform: translateX(100%);
    opacity: 0;
  }
`;

const ToastContainer = styled.div`
  position: fixed;
  top: ${theme.spacing[4]};
  right: ${theme.spacing[4]};
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[3]};
  z-index: ${theme.zIndices.toast};
  max-width: 400px;
  width: 100%;
  pointer-events: none;

  ${theme.media.maxSm} {
    top: auto;
    bottom: ${theme.spacing[4]};
    left: ${theme.spacing[4]};
    right: ${theme.spacing[4]};
    max-width: none;
  }
`;

const toastVariants = {
  success: css`
    background: ${theme.colors.success[50]};
    border-color: ${theme.colors.success[200]};
    color: ${theme.colors.success[800]};

    svg {
      color: ${theme.colors.success[500]};
    }
  `,
  error: css`
    background: ${theme.colors.error[50]};
    border-color: ${theme.colors.error[200]};
    color: ${theme.colors.error[800]};

    svg {
      color: ${theme.colors.error[500]};
    }
  `,
  warning: css`
    background: ${theme.colors.warning[50]};
    border-color: ${theme.colors.warning[200]};
    color: ${theme.colors.warning[800]};

    svg {
      color: ${theme.colors.warning[500]};
    }
  `,
  info: css`
    background: ${theme.colors.primary[50]};
    border-color: ${theme.colors.primary[200]};
    color: ${theme.colors.primary[800]};

    svg {
      color: ${theme.colors.primary[500]};
    }
  `,
};

const StyledToast = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${theme.spacing[3]};
  padding: ${theme.spacing[4]};
  border-radius: ${theme.radii.xl};
  border: 1px solid;
  box-shadow: ${theme.shadows.lg};
  pointer-events: auto;
  animation: ${({ $isExiting }) => ($isExiting ? slideOut : slideIn)} ${theme.durations.slow}
    ${theme.easings.easeOut} forwards;

  ${({ $variant }) => toastVariants[$variant] || toastVariants.info}

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const IconWrapper = styled.div`
  flex-shrink: 0;

  svg {
    width: 20px;
    height: 20px;
  }
`;

const Content = styled.div`
  flex: 1;
  min-width: 0;
`;

const Title = styled.div`
  font-weight: ${theme.fontWeights.semibold};
  font-size: ${theme.fontSizes.sm};
  margin-bottom: ${({ $hasDescription }) => ($hasDescription ? theme.spacing[1] : 0)};
`;

const Description = styled.div`
  font-size: ${theme.fontSizes.sm};
  opacity: 0.9;
`;

const CloseButton = styled.button`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border: none;
  background: transparent;
  cursor: pointer;
  border-radius: ${theme.radii.md};
  opacity: 0.6;
  transition: ${theme.transitions.button};

  &:hover {
    opacity: 1;
    background: rgba(0, 0, 0, 0.05);
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const icons = {
  success: CheckCircle,
  error: AlertCircle,
  warning: AlertTriangle,
  info: Info,
};

const ToastItem = ({ id, variant = 'info', title, description, duration = 5000, onClose }) => {
  const [isExiting, setIsExiting] = useState(false);
  const Icon = icons[variant];

  const handleClose = useCallback(() => {
    setIsExiting(true);
    setTimeout(() => onClose(id), 300);
  }, [id, onClose]);

  useEffect(() => {
    if (duration) {
      const timer = setTimeout(handleClose, duration);
      return () => clearTimeout(timer);
    }
  }, [duration, handleClose]);

  return (
    <StyledToast $variant={variant} $isExiting={isExiting} role="alert">
      <IconWrapper>
        <Icon />
      </IconWrapper>
      <Content>
        <Title $hasDescription={!!description}>{title}</Title>
        {description && <Description>{description}</Description>}
      </Content>
      <CloseButton onClick={handleClose} aria-label="Close notification">
        <X />
      </CloseButton>
    </StyledToast>
  );
};

// Toast Context for global usage
const ToastContext = createContext(null);

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  const addToast = useCallback(({ variant = 'info', title, description, duration = 5000 }) => {
    const id = Date.now() + Math.random();
    setToasts((prev) => [...prev, { id, variant, title, description, duration }]);
    return id;
  }, []);

  const removeToast = useCallback((id) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const toast = {
    show: addToast,
    success: (title, description, duration) => addToast({ variant: 'success', title, description, duration }),
    error: (title, description, duration) => addToast({ variant: 'error', title, description, duration }),
    warning: (title, description, duration) => addToast({ variant: 'warning', title, description, duration }),
    info: (title, description, duration) => addToast({ variant: 'info', title, description, duration }),
  };

  return (
    <ToastContext.Provider value={toast}>
      {children}
      {createPortal(
        <ToastContainer>
          {toasts.map((t) => (
            <ToastItem key={t.id} {...t} onClose={removeToast} />
          ))}
        </ToastContainer>,
        document.body
      )}
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

export const Toast = ToastItem;
Toast.Provider = ToastProvider;
Toast.useToast = useToast;

export default Toast;
